import { LocalFileEntity } from 'components/DocumentsTable/types';
import { subYears } from 'date-fns';
import { isValidName } from 'helpers';
import { isValidNumber } from 'libphonenumber-js';
import {
  ObjectSchema,
  string,
  object,
  FormErrors as FormErrorsBase,
  email,
  TypedForm,
  array,
  date,
  boolean,
} from 'libs/form';
import { TFunc } from 'libs/i18n';
import { localFileScheme } from 'modules/client/common/helpers';

export interface CreateClientFormType {
  owner: {
    brightCode: string;
    email: string;
    firstName: string;
    lastName: string;
    middleName?: string;
    dateOfBirth: Date;
    phoneNumber: string;
    personalAccountNumber: string;
    personalAccountOpeningDate: Date;
  };
  representative: {
    missingRepresentative: boolean;

    email?: string;
    firstName?: string;
    lastName?: string;
    middleName?: string;
    dateOfBirth?: Date;
    phoneNumber?: string;
  };

  companySignedClientServiceAgreementFiles: LocalFileEntity[];
  companySignedNoticeOfInvestmentAccountOpeningFiles: LocalFileEntity[];
}

export const dateOfBirthMaxDate = subYears(new Date(), 18);
export const dateOfBirthMinDate = subYears(new Date(), 100);

export const getSchema = (t: TFunc): ObjectSchema<CreateClientFormType> =>
  object({
    owner: object({
      brightCode: string().required(),
      email: email(t).required(),
      firstName: string()
        .max(20)
        .required()
        .test('ischars', t('validation.noSpecialCharsAndNumber'), isValidName),
      lastName: string()
        .max(20)
        .required()
        .test('ischars', t('validation.noSpecialCharsAndNumber'), isValidName),
      middleName: string()
        .max(20)
        .test('ischars', t('validation.noSpecialCharsAndNumber'), isValidName),
      dateOfBirth: date()
        .min(
          dateOfBirthMinDate,
          t('client.createClient.fields.dateOfBirth.dateOfBirthMinError')
        )
        .max(
          dateOfBirthMaxDate,
          t('client.createClient.fields.dateOfBirth.dateOfBirthMaxError')
        )
        .required(),
      phoneNumber: string()
        .test('officerPhoneNumber', t('validation.phone'), (value) => {
          if (!value) return true;
          return isValidNumber(value);
        })
        .max(20)
        .required(),
      personalAccountNumber: string().max(200).required(),
      personalAccountOpeningDate: date().required(),
    }).required(),

    representative: object({
      missingRepresentative: boolean().required().default(false),

      email: email(t).when('missingRepresentative', {
        is: false,
        then: (schema) => schema.required(),
      }),

      firstName: string().when('missingRepresentative', {
        is: false,
        then: (schema) =>
          schema
            .max(20)
            .required()
            .test(
              'ischars',
              t('validation.noSpecialCharsAndNumber'),
              isValidName
            ),
      }),
      lastName: string().when('missingRepresentative', {
        is: false,
        then: (schema) =>
          schema
            .max(20)
            .required()
            .test(
              'ischars',
              t('validation.noSpecialCharsAndNumber'),
              isValidName
            ),
      }),
      middleName: string().when('missingRepresentative', {
        is: false,
        then: (schema) =>
          schema
            .max(20)
            .test(
              'ischars',
              t('validation.noSpecialCharsAndNumber'),
              isValidName
            ),
      }),
      dateOfBirth: date().when('missingRepresentative', {
        is: false,
        then: (schema) =>
          schema
            .min(
              dateOfBirthMinDate,
              t('client.createClient.fields.dateOfBirth.dateOfBirthMinError')
            )
            .max(
              dateOfBirthMaxDate,
              t('client.createClient.fields.dateOfBirth.dateOfBirthMinError')
            )
            .required(),
      }),

      phoneNumber: string().when('missingRepresentative', {
        is: false,
        then: (schema) =>
          schema
            .test('officerPhoneNumber', t('validation.phone'), (value) => {
              if (!value) return true;
              return isValidNumber(value);
            })
            .max(20)
            .required(),
      }),
    }),

    companySignedClientServiceAgreementFiles: array()
      .of(localFileScheme)
      .required()
      .min(1)
      .max(1),
    companySignedNoticeOfInvestmentAccountOpeningFiles: array()
      .of(localFileScheme)
      .required()
      .min(1)
      .max(2),
  });

export const { Field, Form, useFormContext, Submit } =
  TypedForm<CreateClientFormType>();

export type FormErrors = FormErrorsBase<CreateClientFormType>;
